import {
  AnimatePresence,
  motion,
  PanInfo,
  useDragControls,
  VariantLabels,
} from "framer-motion";
import React, { useContext, useEffect, useRef, useState } from "react";
import {
  PDFDraggablePanelContext,
  ScratchEnvironmentContext,
} from "components/utils/Contexts";
import InstructionsPanelHeader from "./InstructionsPanelHeader";
import Content from "./Content";
import CodeImage from "./CodeImage";
import FullscreenCode from "./FullscreenCode";
import EdgeHandles from "./EdgeHandles";
import { useParams } from "react-router-dom";
import { fetchImage } from "services/filesRequests";
import { LoadingDots } from "utils/loadingAnimation";
import { isImageDataURI } from "utils/utils";

// Constants
/**
 * Used for defining a small "padding" between the borders of the screen
 * and the borders of the PDF panel
 */
const PDF_BORDER_CONSTRAIN = 12; //px
/**
 * Used to define the minimum size of the panel for both, width and height
 */
const MIN_PANEL_WIDTH = 520; // px
const MIN_PANEL_HEIGHT = 290; // px

interface InstructionsDraggablePanelProps {
  handleClosePanel: () => void;
  initial?: object | string | boolean | VariantLabels;
  animate?: object;
  exit?: object;
  style?: object;
  transition?: object;
  panelPosition?: { x: number; y: number };
  setPanelPosition?: React.Dispatch<
    React.SetStateAction<{ x: number; y: number }>
  >;
}

const InstructionsDraggablePanel: React.FC<InstructionsDraggablePanelProps> = ({
  handleClosePanel,
  initial = {},
  animate = {},
  exit = {},
  style = {},
  transition = {},
  panelPosition,
  setPanelPosition,
}) => {
  // Get the states defined by context
  const {
    setIsDraggingResizingPanel,
    stepByStepInstructions,
    currentInstructionsStep,
    setCurrentInstructionsStep,
  } = useContext(ScratchEnvironmentContext);

  // States definition
  const [size, setSize] = useState({
    width: MIN_PANEL_WIDTH,
    height: 0.8 * window.innerHeight,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggable, setIsDraggable] = useState(true);
  const [constraints, setConstraints] = useState({
    top: PDF_BORDER_CONSTRAIN,
    left: PDF_BORDER_CONSTRAIN,
    right: PDF_BORDER_CONSTRAIN,
    bottom: PDF_BORDER_CONSTRAIN,
  });
  const [isResizing, setIsResizing] = useState(false);
  const [selectedStep, setSelectedStep] = useState(currentInstructionsStep);
  // Images states
  const [showCodeFullscreen, setShowCodeFullscreen] = useState(false);
  const [currentStepImageList, setCurrentStepImageList] = useState<{
    [key: number]: string;
  }>({});
  const [fullCodeImageList, setFullCodeImageList] = useState<{
    [key: number]: string;
  }>({});
  const [activeCurrentStepImage, setActiveCurrentStepImage] = useState("");
  const [activeFullCodeImage, setActiveFullCodeImage] = useState("");
  const [initialCodeTab, setInitialCodeTab] = useState(0);
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  // State needed to trigger the listener of the window resizing to
  // re-calculate the constraints of the draggable panel
  const [triggerMeasure, setTriggerMeasure] = useState(false);

  // Draggable controls for the drag icon
  const controls = useDragControls();

  // Define a reference for the PDF panel
  const pdfPanelRef = useRef<HTMLDivElement>(null);

  // Get the url parameters
  const { moduleOrProject, id } = useParams();

  /**
   * Function used to get the instructions images
   * @returns
   */
  const getInstructionsData = async () => {
    // Start with changing this state
    setIsLoadingImages(true);

    // Wrap both processes into an async function
    const getCurrentStep = async () => {
      // Try to get the content
      const loadedImage = currentStepImageList[selectedStep + 1];

      // First of all, check if the selected step is on the images list
      if (loadedImage) {
        // Check if the current step is defined in the imagesList for this step
        setActiveCurrentStepImage(loadedImage || "");
      }
      // Otherwise, check if it's undefined to load the image from AWS
      else if (loadedImage === undefined) {
        // Get the current step image
        const currentStepResponse = await fetchImage(
          `/api/imgs/instructions/${moduleOrProject}/${id}/${
            selectedStep + 1
          }/current-step`
        );

        // If everything went right
        if (currentStepResponse.successful) {
          // Get the data response
          const dataURI = currentStepResponse.content || "";

          // Check if the data received corresponds to an image
          const image = isImageDataURI(dataURI) ? dataURI : "";

          // Add the image to the image list
          setCurrentStepImageList((currentList) => {
            currentList[selectedStep + 1] = image;
            return currentList;
          });

          // Set the state
          setActiveCurrentStepImage(image);
        } else {
          // Otherwise set the empty images
          setActiveCurrentStepImage("");
          setCurrentStepImageList((currentList) => {
            currentList[selectedStep + 1] = "";
            return currentList;
          });
        }
      }
      // But if it's not undefined, then assume that it's an
      // empty string (=== does not exist)
      else {
        setActiveCurrentStepImage("");
      }
    };

    const getFullCode = async () => {
      // Try to get the content
      const loadedImage = fullCodeImageList[selectedStep + 1];

      // First of all, check if the selected step is on the images list
      if (loadedImage) {
        // Check if the current step is defined in the imagesList for this step
        setActiveFullCodeImage(loadedImage || "");
      }
      // Otherwise, check if it's undefined to load the image from AWS
      else if (loadedImage === undefined) {
        // Get the full code image
        const fullCodeResponse = await fetchImage(
          `/api/imgs/instructions/${moduleOrProject}/${id}/${
            selectedStep + 1
          }/full-code`
        );

        // If everything went right
        if (fullCodeResponse.successful) {
          // Get the data response
          const dataURI = fullCodeResponse.content || "";

          // Check if the data received corresponds to an image
          const image = isImageDataURI(dataURI) ? dataURI : "";

          // Add the image to the image list
          setFullCodeImageList((currentList) => {
            currentList[selectedStep + 1] = image;
            return currentList;
          });

          // Set the state
          setActiveFullCodeImage(image);
        } else {
          // Otherwise set the empty images
          setActiveFullCodeImage("");
          setFullCodeImageList((currentList) => {
            currentList[selectedStep + 1] = "";
            return currentList;
          });
        }
      }
      // But if it's not undefined, then assume that it's an
      // empty string (=== does not exist)
      else {
        setActiveFullCodeImage("");
      }
    };

    // And run both of them in parallel to fetch the data
    // more efficiently
    return Promise.all([getCurrentStep(), getFullCode()]);
  };

  /**
   * Function used at the beginning of the rendering of this component
   * to wait until the images are fetched
   */
  useEffect(() => {
    getInstructionsData().then(() => {
      // Useful for the very first loading
      setIsLoading(false);
      // And for stopping the loading images waiting icon animation
      setIsLoadingImages(false);
    });
  }, [selectedStep]);

  /**
   * Function used to update the limits of the panel based on the
   * window size
   */
  useEffect(() => {
    // Dynamically calculate the viewport size
    const updateConstraints = () => {
      setConstraints({
        top: PDF_BORDER_CONSTRAIN,
        left: PDF_BORDER_CONSTRAIN,
        right:
          window.innerWidth -
          PDF_BORDER_CONSTRAIN -
          (pdfPanelRef.current ? pdfPanelRef.current.clientWidth : 0),
        bottom:
          window.innerHeight -
          PDF_BORDER_CONSTRAIN -
          (pdfPanelRef.current ? pdfPanelRef.current.clientHeight : 0),
      });
    };

    // Update constraints on load and resize
    updateConstraints();
    window.addEventListener("resize", updateConstraints);

    return () => window.removeEventListener("resize", updateConstraints);
  }, [triggerMeasure, size]);

  /**
   * Function used to update the panel position every time we are
   * dragging the container around and if a setPanelPosition function
   * is defined
   * @param event
   * @param info
   */
  const handleDragPanel = (
    event: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo
  ) => {
    // Change the cursor
    document.body.style.cursor = "grabbing";

    // Check the existance of the setPanelPosition setter
    if (setPanelPosition && pdfPanelRef.current) {
      // Get the panel information
      const { x, y } = pdfPanelRef.current.getBoundingClientRect();
      setPanelPosition({ x, y });
    }
  };

  /**
   * Function executed after the each animation is completed
   */
  const handleAnimationComplete = () => {
    // Raise the flag for the triggering measure
    setTriggerMeasure(true);
  };

  /**
   * Function used to update the state used to coordinate the definition
   * of the "pointer-events-{x}" property of the Scratch editor
   */
  useEffect(() => {
    setIsDraggingResizingPanel &&
      setIsDraggingResizingPanel(isDragging || isResizing);
  }, [isDragging, isResizing]);

  /**
   * Function used to update the current instructions step
   * for saving the current step in the editor environment
   */
  useEffect(() => {
    setCurrentInstructionsStep(selectedStep);
  }, [selectedStep]);

  return (
    <PDFDraggablePanelContext.Provider
      value={{ MIN_PANEL_WIDTH, MIN_PANEL_HEIGHT }}
    >
      <motion.div
        style={{ ...style, ...size, ...panelPosition }}
        className="fixed w-fit h-fit inset-0 bg-white border border-neutral-50 z-[100] shadow-[0px_0px_8px_0px] shadow-neutral-300 rounded-lg overflow-hidden"
        ref={pdfPanelRef}
        drag={true}
        dragConstraints={constraints}
        dragElastic={0.5}
        dragListener={false}
        dragMomentum={false}
        dragControls={controls}
        initial={isResizing ? undefined : initial}
        animate={
          isResizing
            ? undefined
            : {
                ...panelPosition,
                ...size,
                opacity: showCodeFullscreen ? 0 : 1,
              }
        }
        exit={isResizing ? undefined : exit}
        transition={transition}
        onAnimationComplete={handleAnimationComplete}
        onDrag={handleDragPanel}
      >
        <div className="max-h-screen max-w-[100vw] inline-flex flex-col w-full h-full rounded-lg px-4 py-3 relative">
          <div className="inline-flex flex-col gap-[10px] w-full h-full">
            <InstructionsPanelHeader
              isDraggable={isDraggable}
              isDragging={isDragging}
              setIsDraggable={setIsDraggable}
              setIsDragging={setIsDragging}
              handleClosePanel={handleClosePanel}
              controls={controls}
            />
            <AnimatePresence mode="wait">
              {isLoading ? (
                <motion.div
                  key="loading"
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="w-full h-full inline-flex justify-center items-center"
                >
                  <LoadingDots bgColor="bg-secondary-600" />
                </motion.div>
              ) : (
                <motion.div
                  key="content"
                  className="inline-flex flex-col gap-[10px] w-full h-full"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <CodeImage
                    key={`${selectedStep}-${activeCurrentStepImage}-${activeFullCodeImage}`}
                    currentStepImage={activeCurrentStepImage}
                    fullCodeImage={activeFullCodeImage}
                    isLoadingImages={isLoadingImages}
                    setShowCodeFullscreen={setShowCodeFullscreen}
                    setInitialCodeTab={setInitialCodeTab}
                  />
                  <Content
                    instructions={stepByStepInstructions}
                    activeFullCodeImage={activeFullCodeImage}
                    setShowCodeFullscreen={setShowCodeFullscreen}
                    setInitialCodeTab={setInitialCodeTab}
                    selectedIndex={selectedStep}
                    setSelectedIndex={setSelectedStep}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Edge handles */}
          <EdgeHandles
            pdfPanelRef={pdfPanelRef}
            size={size}
            isResizing={isResizing}
            isDraggable={isDraggable}
            setSize={setSize}
            setIsResizing={setIsResizing}
            setPanelPosition={setPanelPosition}
          />
        </div>
      </motion.div>

      {showCodeFullscreen && (
        <FullscreenCode
          currentStepImage={activeCurrentStepImage}
          fullCodeImage={activeFullCodeImage}
          setShowCodeFullscreen={setShowCodeFullscreen}
          initialCodeTab={initialCodeTab}
        />
      )}
    </PDFDraggablePanelContext.Provider>
  );
};

export default InstructionsDraggablePanel;
